import type { PitchGraphPoint } from '../../components';

/**
 * listeningState — 측정 화면 상단의 **한 줄 상태**. 순수 함수다.
 *
 * 진행 상태(몇 초 지났나)와 다르다. 이 줄은 **마이크가 지금 무엇을 듣고 있나**만 말한다
 * (시안 `882:7830`의 `목소리를 잘 듣고 있어요`). 둘을 한 줄에 섞으면 소리가 끊긴 것과
 * 시간이 남은 것이 같은 자리에서 다투게 된다.
 *
 * 판정은 **유성 프레임의 최신성** 하나로 한다. 음정이 맞았는지는 보지 않는다 — 기준음을
 * 못 맞춰도 소리는 들리고 있고, 그때 "안 들려요"라고 말하면 사용자는 더 크게 부른다
 * (틀린 처방이다). 음이 맞았는지는 트랙의 채움이 말한다.
 *
 * 안 들릴 때는 한 갈래 더 있다. 엔진은 음을 들었는데 **게이트가 레벨 때문에 버린**
 * 프레임이 방금 있었으면 `faint`다 — 소리를 내고 있지만 이 방 기준으로 작다는 뜻이라
 * 처방이 "더 크게"이고, 아무것도 안 들린 `silent`의 처방("소리를 내주세요")과 다르다.
 * 시안도 두 화면을 따로 그린다(§5.4 `884:8299` vs `884:8720`).
 */

/** 마지막 유성 프레임이 이보다 오래됐으면 지금은 안 들리는 것이다. */
export const SILENT_AFTER_SEC = 1.5;

/**
 * 아직 한 번도 안 들렸을 때 "안 들려요"로 넘어가는 시각(초).
 *
 * 시작 직후엔 숨을 고르는 시간이 있으므로 바로 경고하면 안 된다 — 시안의 시작 전 문구
 * (`가능한 만큼 편안하게 유지해 보세요`)가 그 구간을 덮는다.
 */
export const GRACE_SEC = 3;

export type ListeningState = 'idle' | 'hearing' | 'faint' | 'silent';

/**
 * @param points 캡처가 누적한 롤링 창.
 * @param elapsedSec 테이크가 시작된 뒤 흐른 벽시계 시간.
 */
export const listeningState = (
  points: PitchGraphPoint[],
  elapsedSec: number,
  /** 게이트가 레벨 때문에 버린 마지막 프레임의 시각(`LiveSample.faintAtMs`). */
  faintAtMs: number | null = null,
): ListeningState => {
  const latest = points.length ? points[points.length - 1].t : null;
  if (latest === null) {
    // 프레임 자체가 없다 — 세션이 아직 안 열렸거나 캡처가 죽었다. 유예 안에서는
    // 시작 문구를 유지하고, 그 뒤에는 조용하다고 말한다.
    return elapsedSec >= GRACE_SEC ? 'silent' : 'idle';
  }
  // 방금 작은 소리를 들었나. `hearing`보다는 약하고 `silent`보다는 강한 상태다.
  const faintNow =
    faintAtMs != null && latest - faintAtMs <= SILENT_AFTER_SEC * 1000;
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const { t, midi } = points[i];
    if (midi != null) {
      if (latest - t <= SILENT_AFTER_SEC * 1000) {
        return 'hearing';
      }
      return faintNow ? 'faint' : 'silent';
    }
  }
  if (faintNow) {
    return 'faint';
  }
  return elapsedSec >= GRACE_SEC ? 'silent' : 'idle';
};
